import React, { useState, useEffect, useRef } from 'react'
import './CategoriesCharts.css'
//components
import DashboardFilter from '../fragments/DashboardFilter'
import SubcategoriesTable from '../../../components/SubcategoriesTable/SubcategoriesTable'
import Bar from '../../../components/Charts/Bar/Bar'
//services
import { dashboardService } from '../../../services/Dashboard';
//utils
import { formatShortDate, getMonth } from '../../../utils/utils';
import HeaderSection from '../../../components/PdfDownload/Header'

const CategoriesCharts = () => {
  const chartRef = useRef(null);
  const hiddenRef = useRef(null);
  const customColors = ['#15B79E', '#0E5F79', '#F2994A'];
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [lastSubCategories, setLastSubCategories] = useState([]);
  const [reportRange, setReportRange] = useState({ start: null, end: null });
  const [lastSubsPage, setLastSubsPage] = useState(1);
  const [lastSubsTotal, setLastSubsTotal] = useState(0);
  const lastSubsPerPage = 20;
  const [lastSubsFilters, setLastSubsFilters] = useState({});

  useEffect(() => {
    fetchServicesByCategory();
    fetchServicesBySubcategory();
  }, []);

  useEffect(() => {
    fetchLastSubCategories();
  }, [lastSubsPage, lastSubsFilters]);

  const fetchServicesByCategory = async (filters = {}) => {
    try {
      const start_date = filters.rangeDate ? formatShortDate(filters.rangeDate.start_date, '-') : null;
      const end_date = filters.rangeDate ? formatShortDate(filters.rangeDate.end_date, '-') : null;
      const group_by = filters.period ? filters.period.value : null;
      const params = {
        ...(start_date && { start_date }),
        ...(end_date && { end_date }),
        ...(group_by && { group_by })
      };
      setReportRange({ start: start_date, end: end_date });
      const response = await dashboardService.servicesByCategory(params);
      if (response && response.series && response.series[0] && response.series[0].data.length > 0) {
        const data = response.series[0].data.map(item => ({
          x: item.category_name,
          y: item.count
        }));
        setCategories(data);
      }
    } catch (error) {
      console.error('Error fetching services by category:', error);
    }
  };

  const fetchServicesBySubcategory = async (filters = {}) => {
    try {
      const start_date = filters.rangeDate ? formatShortDate(filters.rangeDate.start_date, '-') : null;
      const end_date = filters.rangeDate ? formatShortDate(filters.rangeDate.end_date, '-') : null;
      const group_by = filters.period ? filters.period.value : null;
      const params = {
        ...(start_date && { start_date }),
        ...(end_date && { end_date }),
        ...(group_by && { group_by })
      };
      const response = await dashboardService.servicesBySubcategory(params);
      if (response && response.series && response.series[0] && response.series[0].data.length > 0) {
        const data = response.series[0].data.map(item => ({
          x: item.subcategory_name,
          y: item.count,
          subcategory_name: item.subcategory_name,
          subcategory_id: item.subcategory_id,
          period: item.period
        }));
        setSubCategories(data);
      }
    } catch (error) {
      console.error('Error fetching services by subcategory:', error);
    }
  };

  const fetchLastSubCategories = async () => {
    try {
      const { start_date, end_date, group_by } = lastSubsFilters;
      const params = {
        page: lastSubsPage,
        per_page: lastSubsPerPage,
        ...(start_date ? { start_date } : {}),
        ...(end_date ? { end_date } : {}),
        ...(group_by ? { group_by } : {})
      };
      const response = await dashboardService.lastSubCategories(params);
      if (response && Array.isArray(response.data)) {
        setLastSubCategories(response.data);
        setLastSubsTotal(response.total || 0);
      } else {
        setLastSubCategories([]);
        setLastSubsTotal(0);
      }
    } catch (error) {
      console.error('Error fetching last subcategories:', error);
    }
  };

  return (
    <>
      <div className="packages-charts-container">
        <DashboardFilter
          chartRef={hiddenRef}
          onApplyFilters={(filters) => {
            fetchServicesByCategory(filters);
            fetchServicesBySubcategory(filters);
            fetchLastSubCategories(filters);
          }}
          dynamicHeight={true}
        />
        <div className="chart-wrapper" ref={chartRef}>
          <Bar
            series={[
              {
                data: categories,
                name: 'Categories'
              }
            ]}
            title="Service by category"
            height={170}
            width={400}
            colors={customColors}
          />
          <Bar
            series={[
              {
                data: subCategories,
                name: 'Subcategories'
              }
            ]}
            title="Service by subcategory"
            height={170}
            width={400}
            colors={customColors}
            customTooltip={true}
            tooltipLabelKey="subcategory_name"
            tooltipValueKey="y"
            tooltipValueLabel="Services"
          />
        </div>
        <SubcategoriesTable
          title="Last subcategories added"
          data={lastSubCategories}
          currentPage={lastSubsPage}
          totalItems={lastSubsTotal}
          itemsPerPage={lastSubsPerPage}
          onPageChange={(page) => setLastSubsPage(page)}
          columns={[
            { header: 'Subcategory', field: 'subcategory' },
            { header: 'Category', field: 'category' },
            { header: 'Services', field: 'services' },
            { header: 'Date created', field: 'date_created' },
            { header: 'Status', render: (row) => (row.status ? 'Active' : 'Inactive') },
          ]}
        />
      </div>

      {/* Bloque oculto solo para PDF */}
      <div ref={hiddenRef} className="hidden-container">
        <HeaderSection
          title="Categories"
          date={formatShortDate(new Date(), '-')}
          period={
            reportRange.start && reportRange.end
              ? `${reportRange.start} - ${reportRange.end}`
              : 'Sin fecha'
          }
        />
        <div className="chart-wrapper">
          <Bar
            series={[
              {
                data: categories,
                name: 'Categories'
              }
            ]}
            title="Service by category"
            height={170}
            width={400}
            colors={customColors}
          />
          <Bar
            series={[
              {
                data: subCategories,
                name: 'Subcategories'
              }
            ]}
            title="Service by subcategory"
            height={170}
            width={400}
            colors={customColors}
            customTooltip={true}
            tooltipLabelKey="subcategory_name"
            tooltipValueKey="y"
            tooltipValueLabel="Services"
          />
        </div>
        <SubcategoriesTable
          title="Last subcategories added"
          data={lastSubCategories}
          onPageChange={(page) => setLastSubsPage(page)}
          columns={[
            { header: 'Subcategory', field: 'subcategory' },
            { header: 'Category', field: 'category' },
            { header: 'Services', field: 'services' },
            { header: 'Date created', field: 'date_created' },
            { header: 'Status', render: (row) => (row.status ? 'Active' : 'Inactive') },
          ]}
          tableHeight="500vh"
        />
      </div>
    </>
  )
}

export default CategoriesCharts
